import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBalanceScale,
  faBoxesStacked,
  faCalculator,
  faCashRegister,
  faChartLine,
  faCheckCircle,
  faCircleNodes,
  faFileInvoiceDollar,
  faHandHoldingDollar,
  faReceipt,
  faShieldHalved,
  faStore,
  faTruckRampBox,
  faUsers,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";
import "./ErpSoftware.css";

const canonicalPath = "/erp-software";
const metaTitle = "ERP Software for Complete Business Management | Auditus-iERP";
const metaDescription =
  "Auditus-iERP is ERP software for business billing, accounting, inventory, GST, POS, sales, purchases, customers, suppliers, expenses, and reports.";

const capabilityPills = [
  "Billing",
  "Accounting",
  "Inventory Management",
  "GST",
  "POS",
  "Sales",
  "Purchases",
  "Customer Management",
  "Supplier Management",
  "Business Reports",
];

const connectedModules = [
  "Billing",
  "Accounting",
  "Inventory",
  "GST",
  "POS",
  "Sales",
  "Purchases",
  "Customers",
  "Suppliers",
  "Payments",
  "Expenses",
  "Reports",
];

const features = [
  {
    icon: faFileInvoiceDollar,
    name: "Billing & Invoicing",
    text: "Create GST-ready sales and purchase invoices with practical billing formats.",
    benefit: "Speeds up billing and keeps transaction details organized.",
  },
  {
    icon: faCalculator,
    name: "Accounting",
    text: "Maintain ledgers, vouchers, financial statements, cash flow, and double-entry books.",
    benefit: "Gives owners and accountants a clearer view of business finances.",
  },
  {
    icon: faBoxesStacked,
    name: "Inventory Management",
    text: "Track stock, batches, warehouses, barcode movement, reorder levels, and item reports.",
    benefit: "Helps avoid stock gaps, overstocking, and manual stock confusion.",
  },
  {
    icon: faBalanceScale,
    name: "GST Management",
    text: "Prepare GST reports, manage GSTIN details, HSN/SAC data, e-invoice, and e-way bill workflows.",
    benefit: "Reduces repetitive compliance work and improves tax visibility.",
  },
  {
    icon: faCashRegister,
    name: "POS",
    text: "Handle point-of-sale billing with barcode support, payment options, and quick counter workflows.",
    benefit: "Keeps checkout operations fast for retail teams.",
  },
  {
    icon: faChartLine,
    name: "Sales Management",
    text: "Record sales, returns, discounts, order-based transactions, and item-wise performance.",
    benefit: "Makes revenue tracking easier across daily operations.",
  },
  {
    icon: faTruckRampBox,
    name: "Purchase Management",
    text: "Manage purchase bills, suppliers, goods inward, purchase analysis, and stock updates.",
    benefit: "Connects buying decisions directly with stock and accounts.",
  },
  {
    icon: faUsers,
    name: "Customer Management",
    text: "Keep customer ledgers, addresses, communication records, and payment references in one place.",
    benefit: "Improves follow-up, billing accuracy, and customer service.",
  },
  {
    icon: faWarehouse,
    name: "Supplier Management",
    text: "Track vendor details, purchases, outstanding payments, and supplier-wise reports.",
    benefit: "Supports cleaner procurement and payment planning.",
  },
  {
    icon: faHandHoldingDollar,
    name: "Expense Management",
    text: "Record cash, fund flow, recurring expense entries, and day-to-day business outflow.",
    benefit: "Helps owners understand where operational money is going.",
  },
  {
    icon: faReceipt,
    name: "Business Reports",
    text: "View sales, purchase, inventory, GST, financial, audit, and dashboard reports.",
    benefit: "Turns everyday data into decisions without waiting for manual summaries.",
  },
];

const workflow = ["Purchase", "Inventory", "Sales / Billing", "Payment", "Accounting", "GST / Reports"];

const industries = [
  "Supermarkets",
  "Retail businesses",
  "Textile businesses",
  "Wholesale businesses",
  "Distributors",
  "Pharmacy and medical stores",
  "Garments and fashion stores",
  "Hardware and electricals",
];

const benefits = [
  "Reduce manual work across billing, stock, and accounts",
  "Avoid duplicate data entry between separate tools",
  "Centralize business information for faster access",
  "Improve business visibility with useful reports",
  "Track sales, purchases, payments, and expenses",
  "Handle GST-related workflows from transaction data",
  "Save operational time for owners and staff",
];

const screenshots = [
  {
    src: "/images/screenshots/dashboard.png",
    alt: "Auditus-iERP ERP software dashboard",
    label: "Business Dashboard",
  },
  {
    src: "/images/screenshots/invoice.png",
    alt: "Auditus-iERP billing screen",
    label: "Billing Screen",
  },
  {
    src: "/images/gstdashboard.PNG",
    alt: "Auditus-iERP GST dashboard",
    label: "GST Dashboard",
  },
  {
    src: "/images/screenshots/reports.png",
    alt: "Auditus-iERP business reports screen",
    label: "Reports Screen",
  },
];

const faqs = [
  {
    q: "What is ERP software?",
    a: "ERP software is an integrated system that helps a business manage important operations such as billing, accounting, inventory, purchases, sales, payments, GST, and reports from one place.",
  },
  {
    q: "What does ERP software do?",
    a: "ERP software connects day-to-day business data so that entries made in one area, such as a purchase or sale, can support stock updates, accounts, GST records, and reporting.",
  },
  {
    q: "Is ERP software useful for small businesses?",
    a: "Yes. Small businesses can use ERP software to reduce manual work, organize information, track stock, create bills, manage accounts, and understand business performance more clearly.",
  },
  {
    q: "Can ERP software manage billing and accounting?",
    a: "Yes. Auditus-iERP supports billing and accounting workflows, including invoices, ledgers, vouchers, financial reports, payments, and GST-ready transaction handling.",
  },
  {
    q: "Can ERP software manage inventory?",
    a: "Yes. Auditus-iERP includes inventory management features such as stock tracking, barcode support, batch and warehouse management, reorder alerts, and item-wise reports.",
  },
  {
    q: "Does ERP software support GST?",
    a: "Auditus-iERP supports GST-related workflows including GST-compliant invoicing, GST reports, GSTIN validation references, HSN/SAC handling, e-invoice, and e-way bill processes.",
  },
  {
    q: "Can ERP software be used by retail businesses?",
    a: "Yes. Retail businesses can use Auditus-iERP for POS billing, barcode-based stock movement, sales tracking, customer records, purchases, and daily business reports.",
  },
  {
    q: "What is the difference between ERP software and billing software?",
    a: "Billing software mainly focuses on invoice creation. ERP software connects billing with inventory, accounts, purchases, customers, GST, payments, and reports.",
  },
  {
    q: "What is the difference between ERP software and accounting software?",
    a: "Accounting software focuses on financial records. ERP software includes accounting while also connecting operational areas such as billing, stock, sales, purchases, and GST.",
  },
  {
    q: "How can I get a demo of Auditus-iERP?",
    a: "You can use the Book a Demo button on this page to open the demo request form and share your business details.",
  },
];

function setMeta(name, content, attribute = "name") {
  let tag = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("script");
    tag.id = id;
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

export default function ErpSoftware() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = metaTitle;
    setMeta("description", metaDescription);
    setMeta("robots", "index, follow");
    setMeta("og:title", metaTitle, "property");
    setMeta("og:description", metaDescription, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", canonicalPath, "property");
    setMeta("og:image", "/images/screenshots/dashboard.png", "property");
    setLink("canonical", canonicalPath);

    upsertJsonLd("erp-software-schema", {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "/" },
            { "@type": "ListItem", position: 2, name: "ERP Software", item: canonicalPath },
          ],
        },
        {
          "@type": "SoftwareApplication",
          name: "Auditus-iERP",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Windows, Android",
          url: canonicalPath,
          image: "/images/screenshots/dashboard.png",
          description: metaDescription,
          featureList: features.map((feature) => feature.name),
        },
        {
          "@type": "FAQPage",
          mainEntity: faqs.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        },
      ],
    });

    return () => {
      document.title = previousTitle;
      const schema = document.getElementById("erp-software-schema");
      if (schema) schema.remove();
    };
  }, []);

  return (
    <main className="erp-page">
      <section className="erp-hero" aria-labelledby="erp-hero-title">
        <div className="erp-container erp-hero-grid">
          <div className="erp-hero-copy">
            <nav className="erp-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>ERP Software</span>
            </nav>
            <p className="erp-eyebrow">
              <FontAwesomeIcon icon={faCircleNodes} aria-hidden="true" />
              All-in-one business software
            </p>
            <h1 id="erp-hero-title">ERP Software for Complete Business Management</h1>
            <p className="erp-hero-text">
              Auditus-iERP brings billing, accounting, inventory management, GST, POS,
              sales, purchases, customer management, supplier management, and business
              reports together in one integrated business ERP software.
            </p>
            <div className="erp-hero-actions">
              <Link to="/Demo" className="erp-btn erp-btn-primary">
                Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Trial" className="erp-btn erp-btn-secondary">
                Get Started
              </Link>
            </div>
            <div className="erp-capability-row" aria-label="ERP software capabilities">
              {capabilityPills.map((pill) => (
                <span key={pill}>{pill}</span>
              ))}
            </div>
          </div>

          <div className="erp-hero-visual" aria-label="Auditus-iERP product preview">
            <div className="erp-product-window">
              <div className="erp-window-bar">
                <span>Auditus-iERP</span>
                <i />
              </div>
              <img
                src="/images/screenshots/dashboard.png"
                alt="Auditus-iERP ERP software dashboard"
                width="920"
                height="560"
                loading="eager"
              />
              <div className="erp-window-metrics" aria-label="Connected ERP modules">
                <span>Sales</span>
                <span>Stock</span>
                <span>GST</span>
                <span>Reports</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="erp-section erp-education" aria-labelledby="what-is-erp">
        <div className="erp-container erp-two-col">
          <div>
            <p className="erp-section-kicker">ERP basics</p>
            <h2 id="what-is-erp">What Is ERP Software?</h2>
          </div>
          <div className="erp-rich-text">
            <p>
              ERP software, or enterprise resource planning software, is a system
              that helps a business manage important departments and daily workflows
              through shared data. Instead of keeping billing, accounting, stock,
              purchase, and reporting information in separate software, an ERP system
              connects them.
            </p>
            <p>
              Business owners use ERP software because it gives teams a common place
              to create invoices, update inventory, record purchases, track payments,
              prepare GST information, and view reports. This can reduce duplicate
              entry and make business information easier to trust.
            </p>
          </div>
        </div>
      </section>

      <section className="erp-section erp-connected" aria-labelledby="all-in-one-erp">
        <div className="erp-container">
          <div className="erp-section-header">
            <p className="erp-section-kicker">Auditus-iERP platform</p>
            <h2 id="all-in-one-erp">All-in-One ERP Software for Business Operations</h2>
            <p>
              Auditus-iERP works as complete business management software by linking
              the practical areas that owners, cashiers, purchase teams, stock teams,
              and accountants use every day.
            </p>
          </div>
          <div className="erp-module-map">
            {connectedModules.map((module) => (
              <span key={module}>{module}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="erp-section" aria-labelledby="core-erp-features">
        <div className="erp-container">
          <div className="erp-section-header">
            <p className="erp-section-kicker">Core ERP features</p>
            <h2 id="core-erp-features">Business ERP Software Modules That Work Together</h2>
            <p>
              Each module supports a practical business job while feeding useful data
              into the rest of the ERP system.
            </p>
          </div>
          <div className="erp-feature-grid">
            {features.map((feature) => (
              <article className="erp-feature-card" key={feature.name}>
                <div className="erp-feature-icon">
                  <FontAwesomeIcon icon={feature.icon} aria-hidden="true" />
                </div>
                <h3>{feature.name}</h3>
                <p>{feature.text}</p>
                <strong>{feature.benefit}</strong>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="erp-section erp-workflow-section" aria-labelledby="how-erp-works">
        <div className="erp-container">
          <div className="erp-section-header">
            <p className="erp-section-kicker">Connected workflow</p>
            <h2 id="how-erp-works">How the ERP Works</h2>
            <p>
              In an integrated ERP, information can move from purchase entry to stock,
              billing, payments, accounts, GST, and business reports without rebuilding
              the same data repeatedly.
            </p>
          </div>
          <div className="erp-workflow" aria-label="ERP workflow from purchase to reports">
            {workflow.map((step, index) => (
              <React.Fragment key={step}>
                <div className="erp-workflow-step">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <strong>{step}</strong>
                </div>
                {index < workflow.length - 1 && <div className="erp-workflow-arrow">↓</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="erp-section erp-industries" aria-labelledby="who-can-use">
        <div className="erp-container erp-two-col">
          <div>
            <p className="erp-section-kicker">Business categories</p>
            <h2 id="who-can-use">Who Can Use Auditus-iERP?</h2>
            <p>
              Auditus-iERP is suitable for businesses that need billing and accounting
              software, inventory control, GST workflows, POS, and reports in a single
              business management ERP.
            </p>
          </div>
          <div className="erp-industry-grid">
            {industries.map((industry) => (
              <div className="erp-industry-pill" key={industry}>
                <FontAwesomeIcon icon={faStore} aria-hidden="true" />
                {industry}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="erp-section" aria-labelledby="erp-benefits">
        <div className="erp-container erp-benefit-layout">
          <div>
            <p className="erp-section-kicker">Practical benefits</p>
            <h2 id="erp-benefits">Benefits of ERP Software for Business</h2>
            <p>
              The best ERP software for a growing business should make daily work
              easier, not harder. Auditus-iERP focuses on keeping core information
              connected so teams can spend less time reconciling separate records.
            </p>
          </div>
          <ul className="erp-check-list">
            {benefits.map((benefit) => (
              <li key={benefit}>
                <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="erp-section erp-comparison" aria-labelledby="erp-comparison">
        <div className="erp-container">
          <div className="erp-section-header">
            <p className="erp-section-kicker">Software choice</p>
            <h2 id="erp-comparison">ERP Software vs Separate Billing, Accounting, and Inventory Software</h2>
          </div>
          <div className="erp-compare-grid">
            <article>
              <h3>Separate Software</h3>
              <p>
                Separate billing, accounting, and inventory tools may work for small
                isolated tasks, but teams often need to move information manually
                between systems.
              </p>
              <ul>
                <li>Repeated entry in different places</li>
                <li>More manual checking between reports</li>
                <li>Harder to see one version of business data</li>
              </ul>
            </article>
            <article className="erp-compare-highlight">
              <h3>Integrated ERP Software</h3>
              <p>
                Integrated business ERP software keeps billing, stock, accounts,
                payments, GST, and reports connected around the same transaction flow.
              </p>
              <ul>
                <li>Shared business information</li>
                <li>Connected sales, purchase, and stock data</li>
                <li>Clearer reporting for owners and accounts teams</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className="erp-section" aria-labelledby="product-screenshots">
        <div className="erp-container">
          <div className="erp-section-header">
            <p className="erp-section-kicker">Product screens</p>
            <h2 id="product-screenshots">ERP Software Screenshots</h2>
            <p>
              Real Auditus-iERP assets from the project are used below, including
              dashboard, billing, GST, and reporting screens.
            </p>
          </div>
          <div className="erp-screenshot-grid">
            {screenshots.map((shot) => (
              <figure className="erp-screenshot-card" key={shot.src}>
                <img src={shot.src} alt={shot.alt} loading="lazy" width="640" height="390" />
                <figcaption>{shot.label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="erp-section erp-why" aria-labelledby="why-auditus">
        <div className="erp-container erp-two-col">
          <div>
            <p className="erp-section-kicker">Why Auditus-iERP</p>
            <h2 id="why-auditus">Why Choose Auditus-iERP?</h2>
          </div>
          <div className="erp-rich-text">
            <p>
              Auditus-iERP is built around the everyday needs of businesses that want
              billing ERP software, accounting ERP software, inventory management,
              GST workflows, POS, and reports in one system.
            </p>
            <p>
              It focuses on ease of use and business visibility: create transactions,
              track stock, manage customers and suppliers, record payments and expenses,
              and review reports without separating the same business story across
              disconnected tools.
            </p>
          </div>
        </div>
      </section>

      <section className="erp-section" aria-labelledby="erp-faq">
        <div className="erp-container">
          <div className="erp-section-header">
            <p className="erp-section-kicker">FAQ</p>
            <h2 id="erp-faq">ERP Software Questions</h2>
          </div>
          <div className="erp-faq-list">
            {faqs.map((item) => (
              <details className="erp-faq-item" key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="erp-final-cta" aria-labelledby="erp-final-cta">
        <div className="erp-container">
          <div className="erp-final-panel">
            <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
            <h2 id="erp-final-cta">Ready to Manage Your Business with Auditus-iERP?</h2>
            <p>
              Explore how Auditus-iERP can connect billing, accounts, inventory, GST,
              POS, sales, purchases, and reports for your business.
            </p>
            <div className="erp-hero-actions">
              <Link to="/Demo" className="erp-btn erp-btn-primary">
                Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Support" className="erp-btn erp-btn-secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
